/**
 * Minimap — 2D canvas overview. Dots for fighters/workers, squares for
 * buildings, triangles for resource nodes, and the camera viewport rect.
 * Click to jump the camera.
 */
import type { Combatant } from "../sim/combat";
import type { PlacedBuilding } from "../sim/buildings";
import type { Worker } from "../sim/workers";
import type { ResourceNode } from "../sim/resources";
import type { RtsCamera } from "../render/camera";
import type { VisionLike } from "../render/fog";

const SIZE = 168;

export interface MinimapState {
  fighters: Combatant[];
  buildings: PlacedBuilding[];
  workers: Worker[];
  nodes: ResourceNode[];
  camera: RtsCamera;
  /** Vision system; absent/null until wired by the integrator — then everything is shown. */
  vision?: VisionLike | null;
}

export class Minimap {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private acc = 0;
  /** Full world size in world units (2 × map half-extent); default classic 400. */
  private worldSize: number;

  constructor(worldSize = 400) {
    this.worldSize = worldSize;
    this.canvas = document.createElement("canvas");
    this.canvas.width = SIZE;
    this.canvas.height = SIZE;
    this.canvas.style.cssText =
      "position:fixed;right:12px;top:52px;z-index:30;border:1px solid #3a2f22;" +
      "border-radius:8px;cursor:crosshair;background:rgba(8,10,8,0.9);";
    document.body.appendChild(this.canvas);
    this.ctx = this.canvas.getContext("2d")!;
    const jumpTo = (clientX: number, clientY: number): void => {
      const s = this.state;
      if (!s) return;
      const rect = this.canvas.getBoundingClientRect();
      const px = ((clientX - rect.left) / rect.width) * SIZE;
      const py = ((clientY - rect.top) / rect.height) * SIZE;
      const w = this.worldSize;
      const wx = (px / SIZE) * w - w / 2;
      const wz = (py / SIZE) * w - w / 2;
      const b = w / 2 - 10;
      s.camera.target.x = Math.max(-b, Math.min(b, wx));
      s.camera.target.z = Math.max(-b, Math.min(b, wz));
    };
    this.canvas.addEventListener("mousedown", (e) => jumpTo(e.clientX, e.clientY));
    // Touch: tap or drag on the minimap jumps the camera.
    this.canvas.style.touchAction = "none";
    this.canvas.addEventListener(
      "touchstart",
      (e) => {
        e.preventDefault();
        const t = e.touches[0];
        if (t) jumpTo(t.clientX, t.clientY);
      },
      { passive: false },
    );
    this.canvas.addEventListener(
      "touchmove",
      (e) => {
        e.preventDefault();
        const t = e.touches[0];
        if (t) jumpTo(t.clientX, t.clientY);
      },
      { passive: false },
    );
  }

  private state: MinimapState | null = null;

  bind(state: MinimapState): void {
    this.state = state;
  }

  /** Re-target the minimap at a different world size (loading a save from another size). */
  setWorldSize(worldSize: number): void {
    this.worldSize = worldSize;
  }

  /** Call each render frame; redraws at ~2Hz. */
  update(dt: number): void {
    this.acc += dt;
    if (this.acc < 0.5 || !this.state) return;
    this.acc = 0;
    const s = this.state;
    const ctx = this.ctx;
    const w = this.worldSize;
    const px = (x: number): number => ((x + w / 2) / w) * SIZE;
    const vis = s.vision;
    // When vision is not wired yet, show everything (old behavior).
    const enemySeen = (x: number, z: number): boolean =>
      !vis || vis.isVisible(0, x, z);
    const explored = (x: number, z: number): boolean =>
      !vis || vis.exploredAt(0, x, z);
    this.paintFog(vis);
    // Nodes (terrain features: shown once explored).
    for (const n of s.nodes) {
      if (!explored(n.x, n.z)) continue;
      ctx.fillStyle = n.kind === "timber" ? "#2e5d2a" : "#8a8a8e";
      ctx.fillRect(px(n.x) - 2, px(n.z) - 2, 4, 4);
    }
    // Buildings (enemy buildings hidden until explored, dimmer when unseen).
    for (const b of s.buildings) {
      if (b.team === 1 && !explored(b.x, b.z)) continue;
      const seen = b.team === 0 || enemySeen(b.x, b.z);
      ctx.fillStyle = b.team === 0 ? "#b98b3a" : seen ? "#8c2b23" : "#54201a";
      ctx.fillRect(px(b.x) - 3, px(b.z) - 3, 6, 6);
    }
    // Fighters (enemies only while currently visible).
    for (const f of s.fighters) {
      if (f.team === 1 && !enemySeen(f.view.anchor.x, f.view.anchor.z))
        continue;
      ctx.fillStyle = f.team === 0 ? "#e8e2d4" : "#ff7a5a";
      ctx.beginPath();
      ctx.arc(px(f.view.anchor.x), px(f.view.anchor.z), 2, 0, Math.PI * 2);
      ctx.fill();
    }
    // Workers (enemies only while currently visible).
    ctx.fillStyle = "#7fb3d5";
    for (const w of s.workers) {
      if (w.team === 1 && !enemySeen(w.x, w.z)) continue;
      ctx.fillRect(px(w.x) - 1, px(w.z) - 1, 2, 2);
    }
    // Camera viewport.
    const cx = px(s.camera.target.x);
    const cz = px(s.camera.target.z);
    ctx.strokeStyle = "rgba(232,226,212,0.7)";
    ctx.strokeRect(cx - 22, cz - 16, 44, 32);
  }

  private fogCanvas: HTMLCanvasElement | null = null;
  private fogCtx: CanvasRenderingContext2D | null = null;
  private fogCells = 0;

  /**
   * Paint the fog-of-war background from the vision texture: unexplored is
   * near-black, explored-not-visible is dark olive, visible is dim terrain
   * tone. Falls back to the old flat background when vision is not wired.
   *
   * Assumes the texture is row-major, index = iz * cells + ix, with row 0 at
   * z = -halfExtent (must match the layout VisionSystem.textureData() writes
   * and the uv mapping in world.ts's ground shader).
   */
  private paintFog(vis: VisionLike | null | undefined): void {
    const ctx = this.ctx;
    if (!vis) {
      ctx.clearRect(0, 0, SIZE, SIZE);
      ctx.fillStyle = "rgba(10,14,10,0.9)";
      ctx.fillRect(0, 0, SIZE, SIZE);
      return;
    }
    const cells = vis.cells;
    if (!this.fogCanvas || this.fogCells !== cells) {
      this.fogCanvas = document.createElement("canvas");
      this.fogCanvas.width = cells;
      this.fogCanvas.height = cells;
      this.fogCtx = this.fogCanvas.getContext("2d")!;
      this.fogCells = cells;
    }
    const data = vis.textureData();
    const img = this.fogCtx!.createImageData(cells, cells);
    const out = img.data;
    const n = cells * cells;
    for (let i = 0; i < n; i++) {
      const r = data[i * 4] / 255;
      const g = data[i * 4 + 1] / 255;
      const o = i * 4;
      if (g < 0.5) {
        // Unexplored: near black.
        out[o] = 6;
        out[o + 1] = 8;
        out[o + 2] = 6;
      } else if (r < 0.5) {
        // Explored but not visible: dark olive.
        out[o] = 26;
        out[o + 1] = 34;
        out[o + 2] = 22;
      } else {
        // Visible: dim terrain tone, brighter toward full visibility.
        const l = 0.55 + 0.45 * Math.min(1, r);
        out[o] = 44 * l;
        out[o + 1] = 54 * l;
        out[o + 2] = 32 * l;
      }
      out[o + 3] = 255;
    }
    this.fogCtx!.putImageData(img, 0, 0);
    ctx.imageSmoothingEnabled = true; // soft edges on the minimap too
    ctx.drawImage(this.fogCanvas, 0, 0, SIZE, SIZE);
  }
}
